
import Page from '@/Page.vue';
import { Component } from 'vue-property-decorator';
import moment from 'moment-timezone';

// Timezone pro zobrazování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';
import {
    getScrapSummary,
    getScrapTrendDaily,
    ScrapTrendPoint,
    getScrapTrendDailyByProject,
    ScrapTrendByProjectSeries,
    getScrapTrendDailyByWorkplace,
    ScrapTrendByWorkplaceSeries,
    getScrapTrendDailyByShiftTeamType,
    ScrapTrendByShiftTeamSeries,
    getScrapTrendDailyByShiftType,
    ScrapTrendByShiftTypeSeries,
    getScrapHeatmap,
    ScrapHeatmapResult,
    getTopScrapReasons,
    TopScrapReasonItem,
    getAggregatedScrapData,
    AggregatedScrapItem,
    ScrapAggregationDimension,
} from '@/libs/tpm-analytics/tpm-analytics-data-service';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import TpmFiltersBar from '@/libs/tpm-analytics/components/TpmFiltersBar.vue';
import MissingCycleTimesAlert from '@/libs/tpm-analytics/components/MissingCycleTimesAlert.vue';
import { getDateRange } from '@/libs/tpm-analytics/core/date-range.utils';
import { GlobalVariables } from '@/libs/core/global';
import { tenantsStore } from '@/libs/tenants/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import {
    getFilterById,
    unpackSettings,
    getSelectedFilterLocal,
    setSelectedFilterLocal,
} from '@/libs/tpm-analytics/core/saved-filters';
import {
    saveChartZoom,
    loadChartZoom,
    getOptimalWindowSize,
    clearChartZoom,
} from '@/libs/tpm-analytics/core/chart-zoom-storage';
import { getDayStartMinutes } from '@/libs/tpm-analytics/core/production-day.utils';

// Interface pro datové body s možností uchovat původní hodnotu (pro anomálie)
interface DataPointWithAnomaly {
    x?: number; // Optional - pro kategorickou osu není potřeba
    y: number | null;
    originalValue?: number;
}

@Component({ components: { TpmFiltersBar, MissingCycleTimesAlert } })
export default class TpmAnalyticsScrapAnalysis extends Page {
    totalScraps = 0;
    scrapRate: number | null = null;
    scrapRateDeltaPct: number | null = null;
    scrapCosts = 0;
    avgCostPerPiece = 0;
    scrapDataCompletenessPercent: number | null = null;
    trend: ScrapTrendPoint[] = [];
    trendByProject: ScrapTrendByProjectSeries[] = [];
    trendByWorkplace: ScrapTrendByWorkplaceSeries[] = [];
    trendByShiftTeam: ScrapTrendByShiftTeamSeries[] = [];
    trendByShiftType: ScrapTrendByShiftTypeSeries[] = [];
    // heatmap state
    scrapGrid: number[][] = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
    scrapPalette = ['#e6f7ef', '#fde68a', '#fbbf24', '#f59e0b', '#ef4444'];
    scrapCriticalDays: number[] = [];
    scrapTopHours: number[] = [];
    scrapTotal = 0;
    scrapAvgPerDay = 0;
    topReasons: TopScrapReasonItem[] = [];
    // Aggregated scrap data
    aggregatedScrapData: AggregatedScrapItem[] = [];
    selectedScrapDimensions: ScrapAggregationDimension[] = ['scrapReason'];
    scrapSortBy: 'totalScraps' | 'eventCount' = 'totalScraps';
    aggregatedScrapTop: 10 | 25 | 50 = 10;
    // viewport for trend
    viewStart: number | null = null;
    viewEnd: number | null = null;
    // Pro kategorickou osu: indexy viditelných kategorií
    categoryStartIndex: number | null = null;
    categoryEndIndex: number | null = null;
    // viewport for project trend
    projViewStart: number | null = null;
    projViewEnd: number | null = null;
    // viewport for workplace trend
    wpViewStart: number | null = null;
    wpViewEnd: number | null = null;
    // viewport for shift team trend
    teamViewStart: number | null = null;
    teamViewEnd: number | null = null;
    // viewport for shift type trend
    stViewStart: number | null = null;
    stViewEnd: number | null = null;
    prevScrapRate: number | null = null;
    scrapGranularity: 'day' | 'week' | 'month' = 'day';

    private getTimeUnit(gran: 'day' | 'week' | 'month'): 'day' | 'isoWeek' | 'month' {
        if (gran === 'week') return 'isoWeek';
        if (gran === 'month') return 'month';
        return 'day';
    }
    applyingInitial = false;
    filtersDebounceTimer: any = null;

    // Filters
    periodPreset:
        | 'previousDay'
        | 'last7d'
        | 'last14d'
        | 'last30d'
        | 'last3m'
        | 'thisWeek'
        | 'lastWeek'
        | 'thisMonth'
        | 'lastMonth'
        | 'thisQuarter'
        | 'lastQuarter'
        | 'thisYear'
        | 'lastYear'
        | 'custom' = 'last7d';
    customFrom: string | null = null;
    customTo: string | null = null;
    selectedLocationIds: string[] = [];
    selectedWorkplaceIds: string[] = [];
    selectedProjectIds: string[] = [];
    selectedShiftTypeIds: string[] = [];
    selectedShiftTeamTypeIds: string[] = [];
    get tenantId(): string | null {
        return authStore.useGetter(AuthGetter.GetTenant);
    }

    get windowDays(): number {
        return getOptimalWindowSize(this.scrapGranularity);
    }

    get projWindowDays(): number {
        return getOptimalWindowSize(this.scrapGranularity);
    }

    get wpWindowDays(): number {
        return getOptimalWindowSize(this.scrapGranularity);
    }

    get teamWindowDays(): number {
        return getOptimalWindowSize(this.scrapGranularity);
    }

    get stWindowDays(): number {
        return getOptimalWindowSize(this.scrapGranularity);
    }

    mounted() {
        // V mounted() VŽDY voláme load() - created() pouze nastavil stav
        this.load();
        // Watch pro změnu granularity
        this.$watch('scrapGranularity', (newVal: 'day' | 'week' | 'month', oldVal: 'day' | 'week' | 'month') => {
            if (oldVal && newVal !== oldVal) {
                // Při změně granularity resetujeme všechny zoom
                this.resetViewport();
                this.resetProjectsViewport();
                this.resetWorkplacesViewport();
                this.resetShiftTeamsViewport();
                this.resetShiftTypesViewport();
            }
        });

        // Watch pro změnu agregačních dimenzí
        this.$watch('selectedScrapDimensions', () => {
            // Uložit do localStorage
            try {
                localStorage.setItem('tpm-scrap-aggregation-dimensions', JSON.stringify(this.selectedScrapDimensions));
            } catch (e) {
                console.warn('Nepodařilo se uložit dimenze do localStorage', e);
            }

            // Při změně dimenzí znovu načíst data
            if (this.selectedScrapDimensions.length > 0) {
                this.load();
            }
        });

        // Watch pro změnu řazení
        this.$watch('scrapSortBy', () => {
            // Uložit do localStorage
            try {
                localStorage.setItem('tpm-scrap-aggregation-sortby', this.scrapSortBy);
            } catch (e) {
                console.warn('Nepodařilo se uložit sortBy do localStorage', e);
            }

            // Při změně řazení není potřeba načítat data znovu, jen se změní řazení a TreeMap
            this.$forceUpdate();
        });
    }

    loadScrapAggregationSettings() {
        try {
            // Načíst selectedScrapDimensions
            const savedDimensions = localStorage.getItem('tpm-scrap-aggregation-dimensions');
            if (savedDimensions) {
                try {
                    const dimensions = JSON.parse(savedDimensions) as ScrapAggregationDimension[];
                    if (Array.isArray(dimensions) && dimensions.length > 0) {
                        // Ověřit, že všechny hodnoty jsou platné dimenze
                        const validDimensions: ScrapAggregationDimension[] = [
                            'location',
                            'workplace',
                            'station',
                            'project',
                            'shiftTeam',
                            'shiftType',
                            'scrapReason',
                        ];
                        const filtered = dimensions.filter((d) => validDimensions.includes(d));
                        if (filtered.length > 0) {
                            this.selectedScrapDimensions = filtered;
                        }
                    }
                } catch (parseError) {
                    console.warn('Chyba při parsování dimenzí z localStorage', parseError);
                }
            }

            // Načíst scrapSortBy
            const savedSortBy = localStorage.getItem('tpm-scrap-aggregation-sortby');
            if (savedSortBy && (savedSortBy === 'totalScraps' || savedSortBy === 'eventCount')) {
                this.scrapSortBy = savedSortBy;
            }

            // Načíst aggregatedScrapTop
            const savedAggregatedTop = localStorage.getItem('tpm-aggregated-scrap-top');
            if (savedAggregatedTop && ['10', '25', '50'].includes(savedAggregatedTop)) {
                this.aggregatedScrapTop = parseInt(savedAggregatedTop, 10) as 10 | 25 | 50;
            }
        } catch (e) {
            console.warn('Nepodařilo se načíst nastavení agregace z localStorage', e);
        }
    }

    onAggregatedScrapTopChanged(newTop: 10 | 25 | 50) {
        this.aggregatedScrapTop = newTop;
        // Uložení do localStorage
        try {
            localStorage.setItem('tpm-aggregated-scrap-top', String(newTop));
        } catch (e) {
            console.warn('Nepodařilo se uložit nastavení do localStorage', e);
        }
    }

    getRange() {
        return getDateRange({
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            granularity: this.scrapGranularity,
        });
    }

    async load() {
        if (!this.tenantId) return;
        const { from, to } = this.getRange();
        const filters = {
            locationIds: this.selectedLocationIds,
            workplaceIds: this.selectedWorkplaceIds,
            projectIds: this.selectedProjectIds,
            shiftTypeIds: this.selectedShiftTypeIds,
            shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
        } as any;
        const s = await getScrapSummary(from, to, filters);
        this.totalScraps = s.totalScraps;
        this.scrapRate = s.scrapRate;
        this.scrapRateDeltaPct = s.scrapRateDeltaPct;
        this.scrapCosts = s.scrapCosts;
        this.avgCostPerPiece = s.avgCostPerPiece;
        this.scrapDataCompletenessPercent = s.scrapDataCompleteness?.completenessPercent ?? null;

        this.trend = await getScrapTrendDaily(from, to, filters);
        this.trendByProject = await getScrapTrendDailyByProject(from, to, filters);
        this.trendByWorkplace = await getScrapTrendDailyByWorkplace(from, to, filters);
        this.trendByShiftTeam = await getScrapTrendDailyByShiftTeamType(from, to, filters);
        this.trendByShiftType = await getScrapTrendDailyByShiftType(from, to, filters);

        const hm: ScrapHeatmapResult = await getScrapHeatmap(from, to, filters);
        this.scrapGrid = hm.grid;
        this.scrapCriticalDays = hm.criticalDayIndexes;
        this.scrapTopHours = hm.topHours;
        this.scrapTotal = hm.totalScraps;
        this.scrapAvgPerDay = hm.avgPerDay;

        this.topReasons = await getTopScrapReasons(from, to, 10, filters);

        // Načíst agregovaná data
        this.aggregatedScrapData = await getAggregatedScrapData(from, to, this.selectedScrapDimensions, 9999, filters);

        // previous period for tooltip
        const spanMin = Math.max(1, moment(to).diff(moment(from), 'minutes'));
        const prevTo = moment(from).toISOString();
        const prevFrom = moment(from).add(-spanMin, 'minutes').toISOString();
        const prev = await getScrapSummary(prevFrom, prevTo, filters);
        this.prevScrapRate = prev.scrapRate;

        this.resetViewport();
        this.resetProjectsViewport();
        this.resetWorkplacesViewport();
        this.resetShiftTeamsViewport();
        this.resetShiftTypesViewport();
    }

    onFiltersChanged() {
        if (this.applyingInitial) return;
        // Debounce 200ms - zabráníme duplicitnímu volání při rychlé změně více filtrů
        if (this.filtersDebounceTimer) clearTimeout(this.filtersDebounceTimer);
        this.filtersDebounceTimer = setTimeout(() => {
            this.load();
            const fn = (this.$root as any).$tpmOnStateChanged;
            if (typeof fn === 'function') fn();
        }, 200);
    }
    $watch!: any;
    created() {
        // Načíst nastavení agregace z localStorage (volá se vždy jako výchozí)
        this.loadScrapAggregationSettings();

        // Registrace rozhraní pro parent a načtení stavu z aktuálně vybraného filtru
        (this.$root as any).$tpmGetCurrentState = this.getCurrentFilterState.bind(this);
        (this.$root as any).$tpmApplyState = this.applyFilterState.bind(this);
        const initState = (this.$root as any).$tpmInitialState;
        const initSid = (this.$root as any).$tpmInitialSid;
        if (initState && Object.keys(initState || {}).length > 0) {
            this.applyFilterState(initState, false);
            return;
        }
        if (initSid) {
            setSelectedFilterLocal(initSid);
            getFilterById(initSid).then((model) => {
                const st = unpackSettings((model as any)?.settings) || {};
                this.applyFilterState(st, false);
            });
            return;
        }
        // Bez URL syncu zkus načíst poslední vybraný uložený filtr z local storage
        const localId = getSelectedFilterLocal();
        if (localId) {
            setSelectedFilterLocal(localId);
            getFilterById(localId).then((model) => {
                const st = unpackSettings((model as any)?.settings) || {};
                this.applyFilterState(st, false);
            });
        }
        // Pokud není žádný initial state, mounted() zavolá load() s defaultními hodnotami
    }

    getCurrentFilterState() {
        return {
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            selectedLocationIds: this.selectedLocationIds,
            selectedWorkplaceIds: this.selectedWorkplaceIds,
            selectedProjectIds: this.selectedProjectIds,
            selectedShiftTypeIds: this.selectedShiftTypeIds,
            selectedShiftTeamTypeIds: this.selectedShiftTeamTypeIds,
            granularity: this.scrapGranularity,
        } as any;
    }
    applyFilterState(state: any, reload = true) {
        // reload = true: uživatel změnil filtry → načti data
        // reload = false: inicializace v created() → pouze nastav stav, data se načtou v mounted()
        const periodChanged = state?.periodPreset && state.periodPreset !== this.periodPreset;
        const customDatesChanged =
            (state?.customFrom && state.customFrom !== this.customFrom) ||
            (state?.customTo && state.customTo !== this.customTo);

        if (state?.periodPreset) this.periodPreset = state.periodPreset;
        if (state?.customFrom) this.customFrom = state.customFrom;
        if (state?.customTo) this.customTo = state.customTo;
        this.selectedLocationIds = Array.isArray(state?.selectedLocationIds) ? state.selectedLocationIds : [];
        this.selectedWorkplaceIds = Array.isArray(state?.selectedWorkplaceIds) ? state.selectedWorkplaceIds : [];
        this.selectedProjectIds = Array.isArray(state?.selectedProjectIds) ? state.selectedProjectIds : [];
        this.selectedShiftTypeIds = Array.isArray(state?.selectedShiftTypeIds) ? state.selectedShiftTypeIds : [];
        this.selectedShiftTeamTypeIds = Array.isArray(state?.selectedShiftTeamTypeIds) ? state.selectedShiftTeamTypeIds : [];
        if (state?.granularity) {
            this.scrapGranularity = state.granularity;
        } else if (state?.scrapGranularity) {
            // Zpětná kompatibilita se starými uloženými filtry
            this.scrapGranularity = state.scrapGranularity;
        }

        // Pokud se změnilo období, vymazat uložený zoom aby se použil nový default (maximum dat)
        if (periodChanged || customDatesChanged) {
            clearChartZoom('scrap-trend');
            clearChartZoom('scrap-projects');
            clearChartZoom('scrap-workplaces');
            clearChartZoom('scrap-teams');
            clearChartZoom('scrap-shifttypes');
        }

        if (reload) {
            this.onFiltersChanged();
        }
    }

    formatPct(v: number | null): string {
        if (v == null || isNaN(v as any)) return '-';
        return (v * 100).toFixed(1) + '%';
    }
    formatCost(v: number): string {
        return this.formatCurrency(v);
    }
    get currencyCode(): string {
        try {
            const tenants = tenantsStore.useGetter(CrudGetter.Data) as any[];
            const tenantId = this.tenantId as string;
            const t = tenants?.first
                ? tenants.first((x: any) => x.id === tenantId)
                : (tenants || []).find((x) => x.id === tenantId);
            const code = (t?.currency || '').toString().trim().toUpperCase();
            return code || 'CZK';
        } catch {
            return 'CZK';
        }
    }
    currencyLocale(): string {
        const code = this.currencyCode;
        switch (code) {
            case 'CZK':
                return 'cs-CZ';
            case 'EUR':
                return 'de-DE';
            case 'USD':
                return 'en-US';
            case 'GBP':
                return 'en-GB';
            case 'JPY':
                return 'ja-JP';
            case 'CNY':
                return 'zh-CN';
            case 'CHF':
                return 'de-CH';
            case 'AUD':
                return 'en-AU';
            case 'CAD':
                return 'en-CA';
            case 'SEK':
                return 'sv-SE';
            case 'NOK':
                return 'nb-NO';
            case 'DKK':
                return 'da-DK';
            case 'PLN':
                return 'pl-PL';
            case 'HUF':
                return 'hu-HU';
            case 'INR':
                return 'hi-IN';
            case 'KRW':
                return 'ko-KR';
            case 'HKD':
                return 'zh-HK';
            case 'SGD':
                return 'en-SG';
            case 'NZD':
                return 'en-NZ';
            case 'ZAR':
                return 'en-ZA';
            case 'TRY':
                return 'tr-TR';
            case 'RUB':
                return 'ru-RU';
            case 'AED':
                return 'ar-AE';
            case 'SAR':
                return 'ar-SA';
            case 'ILS':
                return 'he-IL';
            case 'THB':
                return 'th-TH';
            case 'MYR':
                return 'ms-MY';
            case 'IDR':
                return 'id-ID';
            case 'TWD':
                return 'zh-TW';
            case 'VND':
                return 'vi-VN';
            case 'MXN':
                return 'es-MX';
            case 'BRL':
                return 'pt-BR';
            case 'ARS':
                return 'es-AR';
            case 'COP':
                return 'es-CO';
            default:
                return GlobalVariables.lang || 'cs';
        }
    }
    formatCurrency(v: number): string {
        const num = Math.round(Number(v || 0));
        try {
            return new Intl.NumberFormat(this.currencyLocale(), {
                style: 'currency',
                currency: this.currencyCode,
                currencyDisplay: 'symbol',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(num);
        } catch {
            return `${new Intl.NumberFormat('cs').format(num)} ${this.currencyCode}`;
        }
    }
    get currencySymbol(): string {
        try {
            const parts = new Intl.NumberFormat(this.currencyLocale(), {
                style: 'currency',
                currency: this.currencyCode,
                currencyDisplay: 'narrowSymbol',
            }).formatToParts(1);
            return parts.find((p) => p.type === 'currency')?.value || this.currencyCode;
        } catch {
            return this.currencyCode;
        }
    }
    formatPieces(v: number): string {
        const num = Math.round(Number(v || 0));
        return new Intl.NumberFormat(GlobalVariables.lang || 'cs').format(num);
    }

    /**
     * Detekuje outliers (abnormální špičky) v datech a vrací práh pro oříznutí
     * Používá kombinaci více metod pro robustní detekci extrémních i běžných anomálií
     */
    detectOutlierThreshold(values: number[]): number | null {
        const validValues = values.filter((v) => v != null && isFinite(v) && v > 0);
        if (validValues.length < 4) return null;

        // Seřadíme hodnoty
        const sorted = [...validValues].sort((a, b) => a - b);

        // Vypočítáme statistiky
        const q1Index = Math.floor(sorted.length * 0.25);
        const q2Index = Math.floor(sorted.length * 0.5); // medián
        const q3Index = Math.floor(sorted.length * 0.75);
        const p90Index = Math.floor(sorted.length * 0.9);

        const q1 = sorted[q1Index];
        const median = sorted[q2Index];
        const q3 = sorted[q3Index];
        const p90 = sorted[p90Index];
        const iqr = q3 - q1;

        // Metoda 1: IQR metoda s extrémně vysokým faktorem
        // Q3 + 10 * IQR detekuje pouze naprosto extrémní outlery
        const extremeOutlierThreshold = q3 + 10 * iqr;

        // Metoda 2: Extrémně přísný poměrový test
        // Hodnota musí být alespoň 20x větší než Q3, aby byla považována za anomálii
        const ratioThreshold = q3 * 20;

        // Metoda 3: Velmi vysoký adaptivní práh
        // Pro malé hodnoty: 8x medián, pro větší: 10x medián
        const medianBasedThreshold = median > 10 ? median * 10 : median * 8;

        // Metoda 4: 98. percentil s velmi velkou rezervou
        // Použijeme 98. percentil a přidáme ještě větší buffer
        const p98Index = Math.floor(sorted.length * 0.98);
        const p98 = sorted[p98Index];
        const percentileThreshold = p98 + Math.abs(p98 - q3) * 2;

        // Vybereme nejnižší práh z bezpečnostních metod
        const safetyThreshold = Math.min(extremeOutlierThreshold, ratioThreshold, medianBasedThreshold);

        // Finální práh: použijeme nejvíce konzervativní přístup
        const finalThreshold = Math.min(safetyThreshold, percentileThreshold);

        // Dodatečná kontrola: velmi vysoký minimální práh
        // Práh musí být VELMI výrazně nad Q3 - minimálně dvojnásobek nebo Q3 + 5*IQR
        const minThreshold = q3 + Math.max(iqr * 5, q3 * 1.0);

        return Math.max(finalThreshold, minThreshold);
    }

    // Agregovaná data pro scrap trend chart (podle granularity)
    get aggregatedDataForScrapTrend(): Array<{ day: string; rate: number | null }> {
        if (this.scrapGranularity === 'day') {
            return this.trend;
        }

        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;

        const grouped = new Map<string, { sum: number; cnt: number }>();
        const getKey = (d: string) => {
            let periodKey: number;
            if (this.scrapGranularity === 'week') {
                periodKey = moment.tz(d, DISPLAY_TIMEZONE).startOf('isoWeek').valueOf() + dayStartMs;
            } else {
                periodKey = moment.tz(d, DISPLAY_TIMEZONE).startOf('month').valueOf() + dayStartMs;
            }
            return moment.tz(periodKey, DISPLAY_TIMEZONE).toISOString();
        };

        for (const p of this.trend) {
            const k = getKey(p.day);
            const e = grouped.get(k) || { sum: 0, cnt: 0 };
            if (p.rate != null && isFinite(p.rate as any)) {
                e.sum += p.rate;
                e.cnt += 1;
            }
            grouped.set(k, e);
        }

        const aggregated: Array<{ day: string; rate: number | null }> = [];
        grouped.forEach((value, key) => {
            aggregated.push({
                day: key,
                rate: value.cnt > 0 ? value.sum / value.cnt : null,
            });
        });

        return aggregated.sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
    }

    get trendOptions() {
        const self = this;

        // Vygenerujeme kategorie (labely) pro osu X
        const categories = this.aggregatedDataForScrapTrend.map((d) => {
            const dayStartMinutes = getDayStartMinutes();
            const dayStartMs = dayStartMinutes * 60 * 1000;
            let timestamp: number;

            if (this.scrapGranularity === 'week') {
                timestamp = moment.tz(d.day, DISPLAY_TIMEZONE).startOf('isoWeek').valueOf() + dayStartMs;
            } else if (this.scrapGranularity === 'month') {
                timestamp = moment.tz(d.day, DISPLAY_TIMEZONE).startOf('month').valueOf() + dayStartMs;
            } else {
                timestamp = moment.tz(d.day, DISPLAY_TIMEZONE).startOf('day').valueOf() + dayStartMs;
            }

            return self.formatAxisLabel(timestamp, self.scrapGranularity);
        });

        // Najdeme všechny anomálie pro vizuální označení
        const anomalyMarkers: any[] = [];
        if (this.trendSeries && this.trendSeries[0] && this.trendSeries[0].data) {
            this.trendSeries[0].data.forEach((point: any, index: number) => {
                if (point && point.originalValue != null && point.originalValue !== point.y) {
                    anomalyMarkers.push({
                        seriesIndex: 0,
                        dataPointIndex: index,
                        fillColor: '#fbbf24',
                        strokeColor: '#eab308',
                        size: 8,
                        shape: 'circle',
                    });
                }
            });
        }

        return {
            chart: { id: 'scrap-trend', toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false } },
            stroke: { width: 3, curve: 'smooth' },
            markers: {
                size: 4,
                discrete: anomalyMarkers,
            },
            xaxis: {
                type: 'category',
                categories: categories,
                // Pro kategorickou osu je max EXCLUSIVE
                min:
                    this.categoryStartIndex === 0
                        ? -0.8
                        : this.categoryStartIndex != null
                        ? this.categoryStartIndex - 0.3
                        : undefined,
                max: this.categoryEndIndex != null ? this.categoryEndIndex + 1.3 : undefined,
                labels: {
                    rotate: self.scrapGranularity === 'week' ? -45 : 0,
                    formatter(value: any) {
                        // Pro týdny rozdělíme label na dva řádky pomocí |
                        if (self.scrapGranularity === 'week' && typeof value === 'string' && value.includes('|')) {
                            const parts = value.split('|');
                            return parts;
                        }
                        return value;
                    },
                    style: { cssClass: 'apexcharts-xaxis-label-multiline' },
                },
            },
            yaxis: {
                labels: {
                    formatter(v: any) {
                        const n = v == null ? NaN : Number(v);
                        if (!isFinite(n)) return '';
                        return n.toFixed(1) + '%';
                    },
                },
                min: 0,
            },
            grid: {
                padding: {
                    right: 5,
                    left: this.categoryStartIndex === 0 ? 0 : 10,
                },
            },
            tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
                y: {
                    formatter: (val: number) => {
                        if (val == null || isNaN(val)) return '-';
                        return val.toFixed(1) + '%';
                    },
                },
            },
            dataLabels: { enabled: false },
            colors: ['#1f6feb', '#e11d48', '#059669', '#a855f7', '#f59e0b'],
        } as any;
    }
    get trendSeries() {
        console.log('[ScrapTrend] trendSeries called');
        console.log('[ScrapTrend] aggregatedDataForScrapTrend.length:', this.aggregatedDataForScrapTrend.length);
        console.log('[ScrapTrend] trend.length:', this.trend.length);
        console.log('[ScrapTrend] granularity:', this.scrapGranularity);

        // Pro kategorickou osu používáme jednoduché pole čísel
        const data = this.aggregatedDataForScrapTrend.map((d) => {
            if (d.rate == null) return null;
            return +(d.rate * 100).toFixed(2);
        });

        console.log('[ScrapTrend] Final data length:', data.length);
        console.log('[ScrapTrend] Final data sample:', data.slice(0, 5));
        console.log('[ScrapTrend] Final data (all):', data);

        return [
            {
                name: this.$t('tpmAnalytics.scrapRateAvgLabel'),
                data: data,
                type: 'line',
            },
        ] as any;
    }
    get projectTrendOptions() {
        const self = this;

        // Najdeme všechny anomálie pro vizuální označení
        const anomalyAnnotations: any[] = [];
        if (this.projectTrendSeries && Array.isArray(this.projectTrendSeries)) {
            this.projectTrendSeries.forEach((series: any, seriesIndex: number) => {
                if (series.data && Array.isArray(series.data)) {
                    series.data.forEach((point: any) => {
                        if (point.originalValue != null && point.originalValue !== point.y) {
                            anomalyAnnotations.push({
                                x: point.x,
                                y: point.y,
                                marker: {
                                    size: 6,
                                    fillColor: '#fbbf24',
                                    strokeColor: '#fff',
                                    strokeWidth: 2,
                                },
                                label: {
                                    text: '⚠',
                                    style: {
                                        fontSize: '14px',
                                        color: '#000',
                                        background: '#fbbf24',
                                    },
                                    offsetY: -10,
                                },
                            });
                        }
                    });
                }
            });
        }

        return {
            chart: {
                id: 'scrap-trend-projects',
                type: 'bar',
                stacked: false,
                toolbar: { show: false },
                zoom: { enabled: false },
                animations: { enabled: false },
            },
            plotOptions: { bar: { columnWidth: '60%', borderRadius: 4 } },
            dataLabels: { enabled: false },
            annotations: {
                points: anomalyAnnotations,
            },
            tooltip: {
                shared: true,
                intersect: false,
                custom({ series, seriesIndex, dataPointIndex, w }: any) {
                    // Kontrola existence dat
                    if (
                        !w ||
                        !w.config ||
                        !w.config.series ||
                        !Array.isArray(w.config.series) ||
                        w.config.series.length === 0
                    ) {
                        return '';
                    }

                    // Získáme datum z prvního bodu
                    const firstSeries = w.config.series[0];
                    if (!firstSeries || !firstSeries.data || !firstSeries.data[dataPointIndex]) return '';
                    const date = moment.tz(firstSeries.data[dataPointIndex].x, DISPLAY_TIMEZONE).format('D.M.YYYY');

                    // Defaultní barvy jako fallback
                    const defaultColors = [
                        '#1f6feb',
                        '#e11d48',
                        '#059669',
                        '#a855f7',
                        '#f59e0b',
                        '#0891b2',
                        '#ea580c',
                        '#84cc16',
                    ];
                    const colors =
                        w.config.colors && Array.isArray(w.config.colors) && w.config.colors.length > 0
                            ? w.config.colors
                            : defaultColors;

                    // Sesbíráme všechny série pro tento den
                    const allSeriesData: Array<{ name: string; value: number | null; originalValue?: number; color: string }> =
                        [];
                    let hasAnyAnomaly = false;

                    for (let i = 0; i < w.config.series.length; i++) {
                        const seriesData = w.config.series[i];
                        const point = seriesData.data[dataPointIndex] as DataPointWithAnomaly | undefined;
                        if (point) {
                            const originalValue = point.originalValue;
                            const displayValue = point.y;
                            const color = colors[i % colors.length];

                            allSeriesData.push({
                                name: seriesData.name,
                                value: displayValue,
                                originalValue: originalValue,
                                color: color,
                            });

                            if (originalValue != null && originalValue !== displayValue) {
                                hasAnyAnomaly = true;
                            }
                        }
                    }

                    // Seřadíme podle hodnoty (sestupně)
                    allSeriesData.sort((a, b) => (b.value || 0) - (a.value || 0));

                    // Vytvoříme HTML
                    let html = `
                        <div style="background: #fff; padding: 12px 14px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); min-width: 240px; max-width: 320px;">
                            <div style="font-size: 13px; font-weight: 600; color: #111827; margin-bottom: 8px; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px;">
                                ${date}
                            </div>
                    `;

                    // Přidáme všechny série
                    for (const item of allSeriesData) {
                        if (item.value == null) continue;

                        const hasAnomaly = item.originalValue != null && item.originalValue !== item.value;
                        // Zobrazujeme vždy původní hodnotu, pokud existuje
                        const valueToShow = item.originalValue != null ? item.originalValue : item.value;

                        html += `
                            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; padding: 4px 0;">
                                <div style="display: flex; align-items: center; gap: 6px; flex: 1;">
                                    <div style="width: 10px; height: 10px; border-radius: 2px; background: ${
                                        item.color
                                    };"></div>
                                    <span style="font-size: 12px; color: #374151;">${item.name}</span>
                                </div>
                                <div style="text-align: right;">
                                    <span style="font-size: 13px; font-weight: 600; color: #111827;">${valueToShow.toFixed(
                                        1
                                    )}%</span>
                                    ${hasAnomaly ? `<span style="color: #f59e0b; margin-left: 4px;">⚠️</span>` : ''}
                                </div>
                            </div>
                        `;
                    }

                    // Pokud jsou nějaké anomálie, přidáme upozornění
                    if (hasAnyAnomaly) {
                        console.log('💬 [ProjectTooltip] Zobrazuji anomálie v tooltipu:', {
                            datum: date,
                            anomálie: allSeriesData
                                .filter((item) => item.originalValue != null && item.originalValue !== item.value)
                                .map((item) => ({
                                    série: item.name,
                                    původní: item.originalValue?.toFixed(1) + '%',
                                    zobrazená: item.value?.toFixed(1) + '%',
                                })),
                        });

                        html += `
                            <div style="margin-top: 10px; padding: 8px; background: #fef3c7; border-radius: 6px; border-left: 3px solid #f59e0b;">
                                <div style="font-size: 11px; color: #92400e; font-weight: 500; margin-bottom: 4px;">
                                    ⚠️ ${self.$t('tpmAnalytics.dataAnomaly')}
                                </div>
                                <div style="font-size: 11px; color: #78350f; margin-bottom: 4px;">
                                    Data byla upravena pro lepší čitelnost grafu
                                </div>
                        `;

                        // Přidáme seznam úprav pro každou sérii
                        for (const item of allSeriesData) {
                            if (item.originalValue != null && item.originalValue !== item.value) {
                                html += `
                                    <div style="font-size: 10px; color: #78350f; margin-top: 2px; padding-top: 3px; border-top: 1px solid #fcd34d;">
                                        <strong>${item.name}:</strong><br/>
                                        ${item.originalValue.toFixed(1)}% → ${item.value?.toFixed(1)}%
                                    </div>
                                `;
                            }
                        }

                        html += `</div>
                        `;
                    }

                    html += `</div>`;
                    return html;
                },
            },
            xaxis: {
                type: 'datetime',
                min: this.projViewStart ?? undefined,
                max: this.projViewEnd ?? undefined,
                labels: {
                    rotate: this.scrapGranularity === 'week' ? -45 : 0,
                    formatter: (value: any) => {
                        const n = Number(value || 0);
                        if (!isFinite(n)) return '';
                        const label = this.formatAxisLabel(n, this.scrapGranularity);
                        if (this.scrapGranularity === 'week' && label.includes('|')) {
                            return label.split('|');
                        }
                        return label;
                    },
                    style: { cssClass: 'apexcharts-xaxis-label-multiline' },
                },
            },
            yaxis: {
                labels: {
                    formatter(v: any) {
                        const n = v == null ? NaN : Number(v);
                        if (!isFinite(n)) return '';
                        return n.toFixed(1) + '%';
                    },
                },
                min: 0,
            },
            fill: {
                type: 'gradient',
                gradient: { shade: 'light', shadeIntensity: 0.25, opacityFrom: 0.9, opacityTo: 0.75, stops: [0, 100] },
            },
            legend: { show: true, position: 'top', horizontalAlign: 'center' },
        } as any;
    }
    get projectTrendSeries() {
        const src = Array.isArray(this.trendByProject) ? this.trendByProject : [];

        // Nejdřív připravíme všechna data pro všechny projekty
        const allSeriesData = src.map((s) => {
            const grouped = new Map<string, { sum: number; cnt: number }>();
            const getKey = (d: string) => {
                if (this.scrapGranularity === 'month') return moment(d).startOf('month').format('YYYY-MM-01');
                if (this.scrapGranularity === 'week') return moment(d).startOf('isoWeek').format('YYYY-MM-DD');
                return moment(d).startOf('day').format('YYYY-MM-DD');
            };
            for (const p of Array.isArray(s.points) ? s.points : []) {
                const k = getKey(p.day);
                const e = grouped.get(k) || { sum: 0, cnt: 0 };
                if (p.rate != null && isFinite(p.rate as any)) {
                    e.sum += p.rate;
                    e.cnt += 1;
                }
                grouped.set(k, e);
            }
            const keys = Array.from(grouped.keys()).sort();
            return {
                projectName: s.projectName,
                rawData: keys.map((k) => ({
                    x: new Date(k).getTime(),
                    y: grouped.get(k)!.cnt > 0 ? +((grouped.get(k)!.sum / grouped.get(k)!.cnt) * 100).toFixed(2) : null,
                })),
            };
        });

        // Sesbíráme všechny hodnoty ze všech projektů pro výpočet globálního threshold
        const allValues: number[] = [];
        for (const series of allSeriesData) {
            for (const point of series.rawData) {
                if (point.y != null) {
                    allValues.push(point.y);
                }
            }
        }

        // Vypočítáme jeden globální threshold pro všechny projekty
        const globalThreshold = this.detectOutlierThreshold(allValues);

        if (globalThreshold) {
            // Najdeme rozsah dat
            let minDate = Infinity;
            let maxDate = -Infinity;
            for (const series of allSeriesData) {
                for (const point of series.rawData) {
                    if (point.x < minDate) minDate = point.x;
                    if (point.x > maxDate) maxDate = point.x;
                }
            }
            const dateRange =
                minDate !== Infinity
                    ? {
                          od: moment.tz(minDate, DISPLAY_TIMEZONE).format('D.M.YYYY'),
                          do: moment.tz(maxDate, DISPLAY_TIMEZONE).format('D.M.YYYY'),
                          počet: allValues.length,
                          projektů: allSeriesData.length,
                      }
                    : {};
            console.log(
                '🔍 [ProjectTrendSeries] Detekovaný globální anomaly threshold:',
                globalThreshold.toFixed(2) + '%',
                dateRange
            );
        }

        // Aplikujeme threshold na všechny série
        const mapped = allSeriesData.map((series) => ({
            name: (() => {
                const raw = (series.projectName || '').trim();
                if (!raw || raw === '-' || raw === '—' || raw.toLowerCase() === 'n/a') {
                    return this.$t('tpmAnalytics.noProject') as string;
                }
                return raw;
            })(),
            data: series.rawData.map((point) => {
                if (point.y == null || !globalThreshold) {
                    return point;
                }
                if (point.y > globalThreshold) {
                    const projectName = (series.projectName || '').trim() || 'Bez projektu';
                    console.log('⚠️ [ProjectTrendSeries] Anomálie detekována!', {
                        projekt: projectName,
                        datum: moment.tz(point.x, DISPLAY_TIMEZONE).format('D.M.YYYY'),
                        původníHodnota: point.y.toFixed(2) + '%',
                        oříznutáNa: globalThreshold.toFixed(2) + '%',
                    });
                    return {
                        x: point.x,
                        y: +globalThreshold.toFixed(2),
                        originalValue: point.y,
                    } as DataPointWithAnomaly;
                }
                return point;
            }),
        }));
        return mapped.filter((series) => Array.isArray(series.data) && series.data.length > 0) as any;
    }

    get workplaceTrendOptions() {
        const self = this;

        // Najdeme všechny anomálie pro vizuální označení
        const anomalyAnnotations: any[] = [];
        if (this.workplaceTrendSeries && Array.isArray(this.workplaceTrendSeries)) {
            this.workplaceTrendSeries.forEach((series: any) => {
                if (series.data && Array.isArray(series.data)) {
                    series.data.forEach((point: any) => {
                        if (point.originalValue != null && point.originalValue !== point.y) {
                            anomalyAnnotations.push({
                                x: point.x,
                                y: point.y,
                                marker: {
                                    size: 6,
                                    fillColor: '#fbbf24',
                                    strokeColor: '#fff',
                                    strokeWidth: 2,
                                },
                                label: {
                                    text: '⚠',
                                    style: {
                                        fontSize: '14px',
                                        color: '#000',
                                        background: '#fbbf24',
                                    },
                                    offsetY: -10,
                                },
                            });
                        }
                    });
                }
            });
        }

        return {
            chart: {
                id: 'scrap-trend-workplaces',
                type: 'bar',
                stacked: false,
                toolbar: { show: false },
                zoom: { enabled: false },
                animations: { enabled: false },
            },
            plotOptions: { bar: { columnWidth: '60%', borderRadius: 4 } },
            dataLabels: { enabled: false },
            annotations: {
                points: anomalyAnnotations,
            },
            tooltip: {
                shared: true,
                intersect: false,
                custom({ series, seriesIndex, dataPointIndex, w }: any) {
                    if (
                        !w ||
                        !w.config ||
                        !w.config.series ||
                        !Array.isArray(w.config.series) ||
                        w.config.series.length === 0
                    ) {
                        return '';
                    }

                    const firstSeries = w.config.series[0];
                    if (!firstSeries || !firstSeries.data || !firstSeries.data[dataPointIndex]) return '';
                    const date = moment(firstSeries.data[dataPointIndex].x).format('D.M.YYYY');

                    const defaultColors = [
                        '#1f6feb',
                        '#e11d48',
                        '#059669',
                        '#a855f7',
                        '#f59e0b',
                        '#0891b2',
                        '#ea580c',
                        '#84cc16',
                    ];
                    const colors =
                        w.config.colors && Array.isArray(w.config.colors) && w.config.colors.length > 0
                            ? w.config.colors
                            : defaultColors;

                    const allSeriesData: Array<{ name: string; value: number | null; originalValue?: number; color: string }> =
                        [];
                    let hasAnyAnomaly = false;

                    for (let i = 0; i < w.config.series.length; i++) {
                        const seriesData = w.config.series[i];
                        const point = seriesData.data[dataPointIndex] as DataPointWithAnomaly | undefined;
                        if (point) {
                            const originalValue = point.originalValue;
                            const displayValue = point.y;
                            const color = colors[i % colors.length];
                            allSeriesData.push({ name: seriesData.name, value: displayValue, originalValue, color });
                            if (originalValue != null && originalValue !== displayValue) hasAnyAnomaly = true;
                        }
                    }

                    allSeriesData.sort((a, b) => (b.value || 0) - (a.value || 0));

                    let html = `<div style="background: #fff; padding: 12px 14px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); min-width: 240px; max-width: 320px;"><div style="font-size: 13px; font-weight: 600; color: #111827; margin-bottom: 8px; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px;">${date}</div>`;

                    for (const item of allSeriesData) {
                        if (item.value == null) continue;
                        const hasAnomaly = item.originalValue != null && item.originalValue !== item.value;
                        const valueToShow = item.originalValue != null ? item.originalValue : item.value;
                        html += `<div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; padding: 4px 0;"><div style="display: flex; align-items: center; gap: 6px; flex: 1;"><div style="width: 10px; height: 10px; border-radius: 2px; background: ${
                            item.color
                        };"></div><span style="font-size: 12px; color: #374151;">${
                            item.name
                        }</span></div><div style="text-align: right;"><span style="font-size: 13px; font-weight: 600; color: #111827;">${valueToShow.toFixed(
                            1
                        )}%</span>${hasAnomaly ? `<span style="color: #f59e0b; margin-left: 4px;">⚠️</span>` : ''}</div></div>`;
                    }

                    if (hasAnyAnomaly) {
                        html += `<div style="margin-top: 10px; padding: 8px; background: #fef3c7; border-radius: 6px; border-left: 3px solid #f59e0b;"><div style="font-size: 11px; color: #92400e; font-weight: 500; margin-bottom: 4px;">⚠️ ${self.$t(
                            'tpmAnalytics.dataAnomaly'
                        )}</div><div style="font-size: 11px; color: #78350f; margin-bottom: 4px;">Data byla upravena pro lepší čitelnost grafu</div>`;

                        for (const item of allSeriesData) {
                            if (item.originalValue != null && item.originalValue !== item.value) {
                                html += `<div style="font-size: 10px; color: #78350f; margin-top: 2px; padding-top: 3px; border-top: 1px solid #fcd34d;"><strong>${
                                    item.name
                                }:</strong><br/>${item.originalValue.toFixed(1)}% → ${item.value?.toFixed(1)}%</div>`;
                            }
                        }

                        html += `</div>`;
                    }

                    html += `</div>`;
                    return html;
                },
            },
            xaxis: {
                type: 'datetime',
                min: this.wpViewStart ?? undefined,
                max: this.wpViewEnd ?? undefined,
                labels: {
                    rotate: this.scrapGranularity === 'week' ? -45 : 0,
                    formatter: (value: any) => {
                        const n = Number(value || 0);
                        if (!isFinite(n)) return '';
                        const label = this.formatAxisLabel(n, this.scrapGranularity);
                        if (this.scrapGranularity === 'week' && label.includes('|')) {
                            return label.split('|');
                        }
                        return label;
                    },
                    style: { cssClass: 'apexcharts-xaxis-label-multiline' },
                },
            },
            yaxis: {
                labels: {
                    formatter(v: any) {
                        const n = v == null ? NaN : Number(v);
                        if (!isFinite(n)) return '';
                        return n.toFixed(1) + '%';
                    },
                },
                min: 0,
            },
            fill: {
                type: 'gradient',
                gradient: { shade: 'light', shadeIntensity: 0.25, opacityFrom: 0.9, opacityTo: 0.75, stops: [0, 100] },
            },
            legend: { show: true, position: 'top', horizontalAlign: 'center' },
        } as any;
    }
    get workplaceTrendSeries() {
        const src = Array.isArray(this.trendByWorkplace) ? this.trendByWorkplace : [];

        // Připravíme data pro všechna pracoviště
        const allSeriesData = src.map((s) => {
            const grouped = new Map<string, { sum: number; cnt: number }>();
            const getKey = (d: string) => {
                if (this.scrapGranularity === 'month') return moment.tz(d, DISPLAY_TIMEZONE).startOf('month').format('YYYY-MM-01');
                if (this.scrapGranularity === 'week') return moment.tz(d, DISPLAY_TIMEZONE).startOf('isoWeek').format('YYYY-MM-DD');
                return moment.tz(d, DISPLAY_TIMEZONE).startOf('day').format('YYYY-MM-DD');
            };
            for (const p of Array.isArray(s.points) ? s.points : []) {
                const k = getKey(p.day);
                const e = grouped.get(k) || { sum: 0, cnt: 0 };
                if (p.rate != null && isFinite(p.rate as any)) {
                    e.sum += p.rate;
                    e.cnt += 1;
                }
                grouped.set(k, e);
            }
            const keys = Array.from(grouped.keys()).sort();
            return {
                workplaceName: s.workplaceName,
                rawData: keys.map((k) => ({
                    x: new Date(k).getTime(),
                    y: grouped.get(k)!.cnt > 0 ? +((grouped.get(k)!.sum / grouped.get(k)!.cnt) * 100).toFixed(2) : null,
                })),
            };
        });

        // Globální threshold ze všech pracovišť
        const allValues: number[] = [];
        for (const series of allSeriesData) {
            for (const point of series.rawData) {
                if (point.y != null) allValues.push(point.y);
            }
        }
        const globalThreshold = this.detectOutlierThreshold(allValues);

        // Aplikujeme threshold
        const mapped = allSeriesData.map((series) => ({
            name: (() => {
                const raw = (series.workplaceName || '').trim();
                if (!raw || raw === '-' || raw === '—' || raw.toLowerCase() === 'n/a') {
                    return this.$t('tpmAnalytics.noWorkplace') as string;
                }
                return raw;
            })(),
            data: series.rawData.map((point) => {
                if (point.y == null || !globalThreshold) {
                    return point;
                }
                if (point.y > globalThreshold) {
                    return {
                        x: point.x,
                        y: +globalThreshold.toFixed(2),
                        originalValue: point.y,
                    } as DataPointWithAnomaly;
                }
                return point;
            }),
        }));

        return mapped.filter((series) => Array.isArray(series.data) && series.data.length > 0) as any;
    }

    get shiftTeamTrendOptions() {
        const self = this;

        // Najdeme všechny anomálie pro vizuální označení
        const anomalyAnnotations: any[] = [];
        if (this.shiftTeamTrendSeries && Array.isArray(this.shiftTeamTrendSeries)) {
            this.shiftTeamTrendSeries.forEach((series: any) => {
                if (series.data && Array.isArray(series.data)) {
                    series.data.forEach((point: any) => {
                        if (point.originalValue != null && point.originalValue !== point.y) {
                            anomalyAnnotations.push({
                                x: point.x,
                                y: point.y,
                                marker: {
                                    size: 6,
                                    fillColor: '#fbbf24',
                                    strokeColor: '#fff',
                                    strokeWidth: 2,
                                },
                                label: {
                                    text: '⚠',
                                    style: {
                                        fontSize: '14px',
                                        color: '#000',
                                        background: '#fbbf24',
                                    },
                                    offsetY: -10,
                                },
                            });
                        }
                    });
                }
            });
        }

        return {
            chart: {
                id: 'scrap-trend-shift-teams',
                type: 'bar',
                stacked: false,
                toolbar: { show: false },
                zoom: { enabled: false },
                animations: { enabled: false },
            },
            plotOptions: { bar: { columnWidth: '60%', borderRadius: 4 } },
            dataLabels: { enabled: false },
            annotations: {
                points: anomalyAnnotations,
            },
            tooltip: {
                shared: true,
                intersect: false,
                custom({ series, seriesIndex, dataPointIndex, w }: any) {
                    if (
                        !w ||
                        !w.config ||
                        !w.config.series ||
                        !Array.isArray(w.config.series) ||
                        w.config.series.length === 0
                    ) {
                        return '';
                    }

                    const firstSeries = w.config.series[0];
                    if (!firstSeries || !firstSeries.data || !firstSeries.data[dataPointIndex]) return '';
                    const date = moment(firstSeries.data[dataPointIndex].x).format('D.M.YYYY');

                    const defaultColors = [
                        '#1f6feb',
                        '#e11d48',
                        '#059669',
                        '#a855f7',
                        '#f59e0b',
                        '#0891b2',
                        '#ea580c',
                        '#84cc16',
                    ];
                    const colors =
                        w.config.colors && Array.isArray(w.config.colors) && w.config.colors.length > 0
                            ? w.config.colors
                            : defaultColors;

                    const allSeriesData: Array<{ name: string; value: number | null; originalValue?: number; color: string }> =
                        [];
                    let hasAnyAnomaly = false;

                    for (let i = 0; i < w.config.series.length; i++) {
                        const seriesData = w.config.series[i];
                        const point = seriesData.data[dataPointIndex] as DataPointWithAnomaly | undefined;
                        if (point) {
                            const originalValue = point.originalValue;
                            const displayValue = point.y;
                            const color = colors[i % colors.length];
                            allSeriesData.push({ name: seriesData.name, value: displayValue, originalValue, color });
                            if (originalValue != null && originalValue !== displayValue) hasAnyAnomaly = true;
                        }
                    }

                    allSeriesData.sort((a, b) => (b.value || 0) - (a.value || 0));

                    let html = `<div style="background: #fff; padding: 12px 14px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); min-width: 240px; max-width: 320px;"><div style="font-size: 13px; font-weight: 600; color: #111827; margin-bottom: 8px; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px;">${date}</div>`;

                    for (const item of allSeriesData) {
                        if (item.value == null) continue;
                        const hasAnomaly = item.originalValue != null && item.originalValue !== item.value;
                        const valueToShow = item.originalValue != null ? item.originalValue : item.value;
                        html += `<div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; padding: 4px 0;"><div style="display: flex; align-items: center; gap: 6px; flex: 1;"><div style="width: 10px; height: 10px; border-radius: 2px; background: ${
                            item.color
                        };"></div><span style="font-size: 12px; color: #374151;">${
                            item.name
                        }</span></div><div style="text-align: right;"><span style="font-size: 13px; font-weight: 600; color: #111827;">${valueToShow.toFixed(
                            1
                        )}%</span>${hasAnomaly ? `<span style="color: #f59e0b; margin-left: 4px;">⚠️</span>` : ''}</div></div>`;
                    }

                    if (hasAnyAnomaly) {
                        html += `<div style="margin-top: 10px; padding: 8px; background: #fef3c7; border-radius: 6px; border-left: 3px solid #f59e0b;"><div style="font-size: 11px; color: #92400e; font-weight: 500; margin-bottom: 4px;">⚠️ ${self.$t(
                            'tpmAnalytics.dataAnomaly'
                        )}</div><div style="font-size: 11px; color: #78350f; margin-bottom: 4px;">Data byla upravena pro lepší čitelnost grafu</div>`;

                        for (const item of allSeriesData) {
                            if (item.originalValue != null && item.originalValue !== item.value) {
                                html += `<div style="font-size: 10px; color: #78350f; margin-top: 2px; padding-top: 3px; border-top: 1px solid #fcd34d;"><strong>${
                                    item.name
                                }:</strong><br/>${item.originalValue.toFixed(1)}% → ${item.value?.toFixed(1)}%</div>`;
                            }
                        }

                        html += `</div>`;
                    }

                    html += `</div>`;
                    return html;
                },
            },
            xaxis: {
                type: 'datetime',
                min: this.teamViewStart ?? undefined,
                max: this.teamViewEnd ?? undefined,
                labels: {
                    rotate: this.scrapGranularity === 'week' ? -45 : 0,
                    formatter: (value: any) => {
                        const n = Number(value || 0);
                        if (!isFinite(n)) return '';
                        const label = this.formatAxisLabel(n, this.scrapGranularity);
                        if (this.scrapGranularity === 'week' && label.includes('|')) {
                            return label.split('|');
                        }
                        return label;
                    },
                    style: { cssClass: 'apexcharts-xaxis-label-multiline' },
                },
            },
            yaxis: {
                labels: {
                    formatter(v: any) {
                        const n = v == null ? NaN : Number(v);
                        if (!isFinite(n)) return '';
                        return n.toFixed(1) + '%';
                    },
                },
                min: 0,
            },
            fill: {
                type: 'gradient',
                gradient: { shade: 'light', shadeIntensity: 0.25, opacityFrom: 0.9, opacityTo: 0.75, stops: [0, 100] },
            },
            legend: { show: true, position: 'top', horizontalAlign: 'center' },
        } as any;
    }
    get shiftTeamTrendSeries() {
        const src = Array.isArray(this.trendByShiftTeam) ? this.trendByShiftTeam : [];

        // Připravíme data pro všechny směnové týmy
        const allSeriesData = src.map((s) => {
            const grouped = new Map<string, { sum: number; cnt: number }>();
            const getKey = (d: string) => {
                if (this.scrapGranularity === 'month') return moment.tz(d, DISPLAY_TIMEZONE).startOf('month').format('YYYY-MM-01');
                if (this.scrapGranularity === 'week') return moment.tz(d, DISPLAY_TIMEZONE).startOf('isoWeek').format('YYYY-MM-DD');
                return moment.tz(d, DISPLAY_TIMEZONE).startOf('day').format('YYYY-MM-DD');
            };
            for (const p of Array.isArray(s.points) ? s.points : []) {
                const k = getKey(p.day);
                const e = grouped.get(k) || { sum: 0, cnt: 0 };
                if (p.rate != null && isFinite(p.rate as any)) {
                    e.sum += p.rate;
                    e.cnt += 1;
                }
                grouped.set(k, e);
            }
            const keys = Array.from(grouped.keys()).sort();
            return {
                shiftTeamTypeName: s.shiftTeamTypeName,
                rawData: keys.map((k) => ({
                    x: new Date(k).getTime(),
                    y: grouped.get(k)!.cnt > 0 ? +((grouped.get(k)!.sum / grouped.get(k)!.cnt) * 100).toFixed(2) : null,
                })),
            };
        });

        // Globální threshold ze všech směnových týmů
        const allValues: number[] = [];
        for (const series of allSeriesData) {
            for (const point of series.rawData) {
                if (point.y != null) allValues.push(point.y);
            }
        }
        const globalThreshold = this.detectOutlierThreshold(allValues);

        // Aplikujeme threshold
        const mapped = allSeriesData.map((series) => ({
            name: (() => {
                const raw = (series.shiftTeamTypeName || '').trim();
                if (!raw || raw === '-' || raw === '—' || raw.toLowerCase() === 'n/a') {
                    return this.$t('tpmAnalytics.noShiftTeam') as string;
                }
                return raw;
            })(),
            data: series.rawData.map((point) => {
                if (point.y == null || !globalThreshold) {
                    return point;
                }
                if (point.y > globalThreshold) {
                    return {
                        x: point.x,
                        y: +globalThreshold.toFixed(2),
                        originalValue: point.y,
                    } as DataPointWithAnomaly;
                }
                return point;
            }),
        }));

        return mapped.filter((series) => Array.isArray(series.data) && series.data.length > 0) as any;
    }

    get shiftTypeTrendOptions() {
        const self = this;

        // Najdeme všechny anomálie pro vizuální označení
        const anomalyAnnotations: any[] = [];
        if (this.shiftTypeTrendSeries && Array.isArray(this.shiftTypeTrendSeries)) {
            this.shiftTypeTrendSeries.forEach((series: any) => {
                if (series.data && Array.isArray(series.data)) {
                    series.data.forEach((point: any) => {
                        if (point.originalValue != null && point.originalValue !== point.y) {
                            anomalyAnnotations.push({
                                x: point.x,
                                y: point.y,
                                marker: {
                                    size: 6,
                                    fillColor: '#fbbf24',
                                    strokeColor: '#fff',
                                    strokeWidth: 2,
                                },
                                label: {
                                    text: '⚠',
                                    style: {
                                        fontSize: '14px',
                                        color: '#000',
                                        background: '#fbbf24',
                                    },
                                    offsetY: -10,
                                },
                            });
                        }
                    });
                }
            });
        }

        return {
            chart: {
                id: 'scrap-trend-shift-types',
                type: 'bar',
                stacked: false,
                toolbar: { show: false },
                zoom: { enabled: false },
                animations: { enabled: false },
            },
            plotOptions: { bar: { columnWidth: '60%', borderRadius: 4 } },
            dataLabels: { enabled: false },
            annotations: {
                points: anomalyAnnotations,
            },
            tooltip: {
                shared: true,
                intersect: false,
                custom({ series, seriesIndex, dataPointIndex, w }: any) {
                    if (
                        !w ||
                        !w.config ||
                        !w.config.series ||
                        !Array.isArray(w.config.series) ||
                        w.config.series.length === 0
                    ) {
                        return '';
                    }

                    const firstSeries = w.config.series[0];
                    if (!firstSeries || !firstSeries.data || !firstSeries.data[dataPointIndex]) return '';
                    const date = moment(firstSeries.data[dataPointIndex].x).format('D.M.YYYY');

                    const defaultColors = [
                        '#1f6feb',
                        '#e11d48',
                        '#059669',
                        '#a855f7',
                        '#f59e0b',
                        '#0891b2',
                        '#ea580c',
                        '#84cc16',
                    ];
                    const colors =
                        w.config.colors && Array.isArray(w.config.colors) && w.config.colors.length > 0
                            ? w.config.colors
                            : defaultColors;

                    const allSeriesData: Array<{ name: string; value: number | null; originalValue?: number; color: string }> =
                        [];
                    let hasAnyAnomaly = false;

                    for (let i = 0; i < w.config.series.length; i++) {
                        const seriesData = w.config.series[i];
                        const point = seriesData.data[dataPointIndex] as DataPointWithAnomaly | undefined;
                        if (point) {
                            const originalValue = point.originalValue;
                            const displayValue = point.y;
                            const color = colors[i % colors.length];
                            allSeriesData.push({ name: seriesData.name, value: displayValue, originalValue, color });
                            if (originalValue != null && originalValue !== displayValue) hasAnyAnomaly = true;
                        }
                    }

                    allSeriesData.sort((a, b) => (b.value || 0) - (a.value || 0));

                    let html = `<div style="background: #fff; padding: 12px 14px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); min-width: 240px; max-width: 320px;"><div style="font-size: 13px; font-weight: 600; color: #111827; margin-bottom: 8px; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px;">${date}</div>`;

                    for (const item of allSeriesData) {
                        if (item.value == null) continue;
                        const hasAnomaly = item.originalValue != null && item.originalValue !== item.value;
                        const valueToShow = item.originalValue != null ? item.originalValue : item.value;
                        html += `<div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; padding: 4px 0;"><div style="display: flex; align-items: center; gap: 6px; flex: 1;"><div style="width: 10px; height: 10px; border-radius: 2px; background: ${
                            item.color
                        };"></div><span style="font-size: 12px; color: #374151;">${
                            item.name
                        }</span></div><div style="text-align: right;"><span style="font-size: 13px; font-weight: 600; color: #111827;">${valueToShow.toFixed(
                            1
                        )}%</span>${hasAnomaly ? `<span style="color: #f59e0b; margin-left: 4px;">⚠️</span>` : ''}</div></div>`;
                    }

                    if (hasAnyAnomaly) {
                        html += `<div style="margin-top: 10px; padding: 8px; background: #fef3c7; border-radius: 6px; border-left: 3px solid #f59e0b;"><div style="font-size: 11px; color: #92400e; font-weight: 500; margin-bottom: 4px;">⚠️ ${self.$t(
                            'tpmAnalytics.dataAnomaly'
                        )}</div><div style="font-size: 11px; color: #78350f; margin-bottom: 4px;">Data byla upravena pro lepší čitelnost grafu</div>`;

                        for (const item of allSeriesData) {
                            if (item.originalValue != null && item.originalValue !== item.value) {
                                html += `<div style="font-size: 10px; color: #78350f; margin-top: 2px; padding-top: 3px; border-top: 1px solid #fcd34d;"><strong>${
                                    item.name
                                }:</strong><br/>${item.originalValue.toFixed(1)}% → ${item.value?.toFixed(1)}%</div>`;
                            }
                        }

                        html += `</div>`;
                    }

                    html += `</div>`;
                    return html;
                },
            },
            xaxis: {
                type: 'datetime',
                min: this.stViewStart ?? undefined,
                max: this.stViewEnd ?? undefined,
                labels: {
                    rotate: this.scrapGranularity === 'week' ? -45 : 0,
                    formatter: (value: any) => {
                        const n = Number(value || 0);
                        if (!isFinite(n)) return '';
                        const label = this.formatAxisLabel(n, this.scrapGranularity);
                        // Pro týdny rozdělíme label na dva řádky pomocí |
                        if (this.scrapGranularity === 'week' && label.includes('|')) {
                            return label.split('|');
                        }
                        return label;
                    },
                    style: { cssClass: 'apexcharts-xaxis-label-multiline' },
                },
            },
            yaxis: {
                labels: {
                    formatter(v: any) {
                        const n = v == null ? NaN : Number(v);
                        if (!isFinite(n)) return '';
                        return n.toFixed(1) + '%';
                    },
                },
                min: 0,
            },
            fill: {
                type: 'gradient',
                gradient: { shade: 'light', shadeIntensity: 0.25, opacityFrom: 0.9, opacityTo: 0.75, stops: [0, 100] },
            },
            legend: { show: true, position: 'top', horizontalAlign: 'center' },
        } as any;
    }
    get shiftTypeTrendSeries() {
        const src = Array.isArray(this.trendByShiftType) ? this.trendByShiftType : [];

        // Připravíme data pro všechny typy směn
        const allSeriesData = src.map((s) => {
            const grouped = new Map<string, { sum: number; cnt: number }>();
            const getKey = (d: string) => {
                if (this.scrapGranularity === 'month') return moment.tz(d, DISPLAY_TIMEZONE).startOf('month').format('YYYY-MM-01');
                if (this.scrapGranularity === 'week') return moment.tz(d, DISPLAY_TIMEZONE).startOf('isoWeek').format('YYYY-MM-DD');
                return moment.tz(d, DISPLAY_TIMEZONE).startOf('day').format('YYYY-MM-DD');
            };
            for (const p of Array.isArray(s.points) ? s.points : []) {
                const k = getKey(p.day);
                const e = grouped.get(k) || { sum: 0, cnt: 0 };
                if (p.rate != null && isFinite(p.rate as any)) {
                    e.sum += p.rate;
                    e.cnt += 1;
                }
                grouped.set(k, e);
            }
            const keys = Array.from(grouped.keys()).sort();
            return {
                shiftTypeName: s.shiftTypeName,
                rawData: keys.map((k) => ({
                    x: new Date(k).getTime(),
                    y: grouped.get(k)!.cnt > 0 ? +((grouped.get(k)!.sum / grouped.get(k)!.cnt) * 100).toFixed(2) : null,
                })),
            };
        });

        // Globální threshold ze všech typů směn
        const allValues: number[] = [];
        for (const series of allSeriesData) {
            for (const point of series.rawData) {
                if (point.y != null) allValues.push(point.y);
            }
        }
        const globalThreshold = this.detectOutlierThreshold(allValues);

        // Aplikujeme threshold
        const mapped = allSeriesData.map((series) => ({
            name: (() => {
                const raw = (series.shiftTypeName || '').trim();
                if (!raw || raw === '-' || raw === '—' || raw.toLowerCase() === 'n/a') {
                    return this.$t('tpmAnalytics.noShiftType') as string;
                }
                return raw;
            })(),
            data: series.rawData.map((point) => {
                if (point.y == null || !globalThreshold) {
                    return point;
                }
                if (point.y > globalThreshold) {
                    return {
                        x: point.x,
                        y: +globalThreshold.toFixed(2),
                        originalValue: point.y,
                    } as DataPointWithAnomaly;
                }
                return point;
            }),
        }));

        return mapped.filter((series) => Array.isArray(series.data) && series.data.length > 0) as any;
    }
    downloadChart(_id: string) {
        /* reserved for export */
    }

    resetViewport() {
        const data = this.aggregatedDataForScrapTrend;
        if (data.length === 0) {
            this.categoryStartIndex = null;
            this.categoryEndIndex = null;
            return;
        }

        // Pro kategorickou osu: zobrazíme všechna data (null = všechno)
        this.categoryStartIndex = null;
        this.categoryEndIndex = null;
    }
    resetProjectsViewport() {
        const allPoints = this.trendByProject.flatMap((s) => s.points);
        if (allPoints.length === 0) {
            this.projViewStart = null;
            this.projViewEnd = null;
            return;
        }

        // Zobrazíme vždy všechna dostupná data (maximální zoom)
        const unit = this.getTimeUnit(this.scrapGranularity);
        this.projViewStart = moment.tz(allPoints[0].day, DISPLAY_TIMEZONE).startOf(unit).valueOf();
        this.projViewEnd = moment.tz(allPoints[allPoints.length - 1].day, DISPLAY_TIMEZONE)
            .endOf(unit)
            .valueOf();
    }
    resetWorkplacesViewport() {
        const allPoints = this.trendByWorkplace.flatMap((s) => s.points);
        if (allPoints.length === 0) {
            this.wpViewStart = null;
            this.wpViewEnd = null;
            return;
        }

        // Zobrazíme vždy všechna dostupná data (maximální zoom)
        const unit = this.getTimeUnit(this.scrapGranularity);
        this.wpViewStart = moment.tz(allPoints[0].day, DISPLAY_TIMEZONE).startOf(unit).valueOf();
        this.wpViewEnd = moment.tz(allPoints[allPoints.length - 1].day, DISPLAY_TIMEZONE)
            .endOf(unit)
            .valueOf();
    }
    resetShiftTeamsViewport() {
        const allPoints = this.trendByShiftTeam.flatMap((s) => s.points);
        if (allPoints.length === 0) {
            this.teamViewStart = null;
            this.teamViewEnd = null;
            return;
        }

        // Zobrazíme vždy všechna dostupná data (maximální zoom)
        const unit = this.getTimeUnit(this.scrapGranularity);
        this.teamViewStart = moment.tz(allPoints[0].day, DISPLAY_TIMEZONE).startOf(unit).valueOf();
        this.teamViewEnd = moment.tz(allPoints[allPoints.length - 1].day, DISPLAY_TIMEZONE)
            .endOf(unit)
            .valueOf();
    }
    teamShiftPercent(percent: number) {
        const allPoints = this.trendByShiftTeam.flatMap((s) => s.points);
        if (allPoints.length === 0 || this.teamViewStart == null || this.teamViewEnd == null) return;
        const totalStart = moment.tz(allPoints[0].day, DISPLAY_TIMEZONE).startOf('day').valueOf();
        const totalEnd = moment.tz(allPoints[allPoints.length - 1].day, DISPLAY_TIMEZONE)
            .endOf('day')
            .valueOf();
        const span = this.teamViewEnd - this.teamViewStart;
        const delta = Math.round(span * percent);
        let newStart = this.teamViewStart + delta;
        let newEnd = this.teamViewEnd + delta;
        if (newStart < totalStart) {
            newStart = totalStart;
            newEnd = totalStart + span;
        }
        if (newEnd > totalEnd) {
            newEnd = totalEnd;
            newStart = totalEnd - span;
        }
        this.teamViewStart = newStart;
        this.teamViewEnd = newEnd;
        this.saveCurrentTeamsZoom();
    }
    teamZoomIn() {
        const allPoints = this.trendByShiftTeam.flatMap((s) => s.points);
        if (allPoints.length === 0 || this.teamViewStart == null || this.teamViewEnd == null) return;
        const totalStart = moment.tz(allPoints[0].day, DISPLAY_TIMEZONE).startOf('day').valueOf();
        const totalEnd = moment.tz(allPoints[allPoints.length - 1].day, DISPLAY_TIMEZONE)
            .endOf('day')
            .valueOf();
        const span = this.teamViewEnd - this.teamViewStart;
        const minSpan = 2 * 24 * 60 * 60 * 1000;
        const newSpan = Math.max(minSpan, Math.round(span * 0.75));
        const center = Math.round((this.teamViewStart + this.teamViewEnd) / 2);
        let newStart = center - Math.round(newSpan / 2);
        let newEnd = center + Math.round(newSpan / 2);
        if (newStart < totalStart) {
            newStart = totalStart;
            newEnd = newStart + newSpan;
        }
        if (newEnd > totalEnd) {
            newEnd = totalEnd;
            newStart = newEnd - newSpan;
        }
        this.teamViewStart = newStart;
        this.teamViewEnd = newEnd;
        this.saveCurrentTeamsZoom();
    }
    teamZoomOut() {
        const allPoints = this.trendByShiftTeam.flatMap((s) => s.points);
        if (allPoints.length === 0 || this.teamViewStart == null || this.teamViewEnd == null) return;
        const totalStart = moment.tz(allPoints[0].day, DISPLAY_TIMEZONE).startOf('day').valueOf();
        const totalEnd = moment.tz(allPoints[allPoints.length - 1].day, DISPLAY_TIMEZONE)
            .endOf('day')
            .valueOf();
        const span = this.teamViewEnd - this.teamViewStart;
        const newSpan = Math.min(totalEnd - totalStart, Math.round(span / 0.75));
        const center = Math.round((this.teamViewStart + this.teamViewEnd) / 2);
        let newStart = center - Math.round(newSpan / 2);
        let newEnd = center + Math.round(newSpan / 2);
        if (newStart < totalStart) {
            newStart = totalStart;
            newEnd = newStart + newSpan;
        }
        if (newEnd > totalEnd) {
            newEnd = totalEnd;
            newStart = newEnd - newSpan;
        }
        this.teamViewStart = newStart;
        this.teamViewEnd = newEnd;
        this.saveCurrentTeamsZoom();
    }

    resetShiftTypesViewport() {
        const allPoints = this.trendByShiftType.flatMap((s) => s.points);
        if (allPoints.length === 0) {
            this.stViewStart = null;
            this.stViewEnd = null;
            return;
        }

        // Zobrazíme vždy všechna dostupná data (maximální zoom)
        const unit = this.getTimeUnit(this.scrapGranularity);
        this.stViewStart = moment.tz(allPoints[0].day, DISPLAY_TIMEZONE).startOf(unit).valueOf();
        this.stViewEnd = moment.tz(allPoints[allPoints.length - 1].day, DISPLAY_TIMEZONE)
            .endOf(unit)
            .valueOf();
    }
    stShiftPercent(percent: number) {
        const allPoints = this.trendByShiftType.flatMap((s) => s.points);
        if (allPoints.length === 0 || this.stViewStart == null || this.stViewEnd == null) return;
        const totalStart = moment.tz(allPoints[0].day, DISPLAY_TIMEZONE).startOf('day').valueOf();
        const totalEnd = moment.tz(allPoints[allPoints.length - 1].day, DISPLAY_TIMEZONE)
            .endOf('day')
            .valueOf();
        const span = this.stViewEnd - this.stViewStart;
        const delta = Math.round(span * percent);
        let newStart = this.stViewStart + delta;
        let newEnd = this.stViewEnd + delta;
        if (newStart < totalStart) {
            newStart = totalStart;
            newEnd = totalStart + span;
        }
        if (newEnd > totalEnd) {
            newEnd = totalEnd;
            newStart = totalEnd - span;
        }
        this.stViewStart = newStart;
        this.stViewEnd = newEnd;
        this.saveCurrentShiftTypesZoom();
    }
    stZoomIn() {
        const allPoints = this.trendByShiftType.flatMap((s) => s.points);
        if (allPoints.length === 0 || this.stViewStart == null || this.stViewEnd == null) return;
        const totalStart = moment.tz(allPoints[0].day, DISPLAY_TIMEZONE).startOf('day').valueOf();
        const totalEnd = moment.tz(allPoints[allPoints.length - 1].day, DISPLAY_TIMEZONE)
            .endOf('day')
            .valueOf();
        const span = this.stViewEnd - this.stViewStart;
        const minSpan = 2 * 24 * 60 * 60 * 1000;
        const newSpan = Math.max(minSpan, Math.round(span * 0.75));
        const center = Math.round((this.stViewStart + this.stViewEnd) / 2);
        let newStart = center - Math.round(newSpan / 2);
        let newEnd = center + Math.round(newSpan / 2);
        if (newStart < totalStart) {
            newStart = totalStart;
            newEnd = newStart + newSpan;
        }
        if (newEnd > totalEnd) {
            newEnd = totalEnd;
            newStart = newEnd - newSpan;
        }
        this.stViewStart = newStart;
        this.stViewEnd = newEnd;
        this.saveCurrentShiftTypesZoom();
    }
    stZoomOut() {
        const allPoints = this.trendByShiftType.flatMap((s) => s.points);
        if (allPoints.length === 0 || this.stViewStart == null || this.stViewEnd == null) return;
        const totalStart = moment.tz(allPoints[0].day, DISPLAY_TIMEZONE).startOf('day').valueOf();
        const totalEnd = moment.tz(allPoints[allPoints.length - 1].day, DISPLAY_TIMEZONE)
            .endOf('day')
            .valueOf();
        const span = this.stViewEnd - this.stViewStart;
        const newSpan = Math.min(totalEnd - totalStart, Math.round(span / 0.75));
        const center = Math.round((this.stViewStart + this.stViewEnd) / 2);
        let newStart = center - Math.round(newSpan / 2);
        let newEnd = center + Math.round(newSpan / 2);
        if (newStart < totalStart) {
            newStart = totalStart;
            newEnd = newStart + newSpan;
        }
        if (newEnd > totalEnd) {
            newEnd = totalEnd;
            newStart = newEnd - newSpan;
        }
        this.stViewStart = newStart;
        this.stViewEnd = newEnd;
        this.saveCurrentShiftTypesZoom();
    }
    wpShiftPercent(percent: number) {
        const allPoints = this.trendByWorkplace.flatMap((s) => s.points);
        if (allPoints.length === 0 || this.wpViewStart == null || this.wpViewEnd == null) return;
        const totalStart = moment(allPoints[0].day).startOf('day').valueOf();
        const totalEnd = moment(allPoints[allPoints.length - 1].day)
            .endOf('day')
            .valueOf();
        const span = this.wpViewEnd - this.wpViewStart;
        const delta = Math.round(span * percent);
        let newStart = this.wpViewStart + delta;
        let newEnd = this.wpViewEnd + delta;
        if (newStart < totalStart) {
            newStart = totalStart;
            newEnd = totalStart + span;
        }
        if (newEnd > totalEnd) {
            newEnd = totalEnd;
            newStart = totalEnd - span;
        }
        this.wpViewStart = newStart;
        this.wpViewEnd = newEnd;
        this.saveCurrentWorkplacesZoom();
    }
    wpZoomIn() {
        const allPoints = this.trendByWorkplace.flatMap((s) => s.points);
        if (allPoints.length === 0 || this.wpViewStart == null || this.wpViewEnd == null) return;
        const totalStart = moment(allPoints[0].day).startOf('day').valueOf();
        const totalEnd = moment(allPoints[allPoints.length - 1].day)
            .endOf('day')
            .valueOf();
        const span = this.wpViewEnd - this.wpViewStart;
        const minSpan = 2 * 24 * 60 * 60 * 1000;
        const newSpan = Math.max(minSpan, Math.round(span * 0.75));
        const center = Math.round((this.wpViewStart + this.wpViewEnd) / 2);
        let newStart = center - Math.round(newSpan / 2);
        let newEnd = center + Math.round(newSpan / 2);
        if (newStart < totalStart) {
            newStart = totalStart;
            newEnd = newStart + newSpan;
        }
        if (newEnd > totalEnd) {
            newEnd = totalEnd;
            newStart = newEnd - newSpan;
        }
        this.wpViewStart = newStart;
        this.wpViewEnd = newEnd;
        this.saveCurrentWorkplacesZoom();
    }
    wpZoomOut() {
        const allPoints = this.trendByWorkplace.flatMap((s) => s.points);
        if (allPoints.length === 0 || this.wpViewStart == null || this.wpViewEnd == null) return;
        const totalStart = moment(allPoints[0].day).startOf('day').valueOf();
        const totalEnd = moment(allPoints[allPoints.length - 1].day)
            .endOf('day')
            .valueOf();
        const span = this.wpViewEnd - this.wpViewStart;
        const newSpan = Math.min(totalEnd - totalStart, Math.round(span / 0.75));
        const center = Math.round((this.wpViewStart + this.wpViewEnd) / 2);
        let newStart = center - Math.round(newSpan / 2);
        let newEnd = center + Math.round(newSpan / 2);
        if (newStart < totalStart) {
            newStart = totalStart;
            newEnd = newStart + newSpan;
        }
        if (newEnd > totalEnd) {
            newEnd = totalEnd;
            newStart = newEnd - newSpan;
        }
        this.wpViewStart = newStart;
        this.wpViewEnd = newEnd;
        this.saveCurrentWorkplacesZoom();
    }
    projShiftPercent(percent: number) {
        const allPoints = this.trendByProject.flatMap((s) => s.points);
        if (allPoints.length === 0 || this.projViewStart == null || this.projViewEnd == null) return;
        const totalStart = moment(allPoints[0].day).startOf('day').valueOf();
        const totalEnd = moment(allPoints[allPoints.length - 1].day)
            .endOf('day')
            .valueOf();
        const span = this.projViewEnd - this.projViewStart;
        const delta = Math.round(span * percent);
        let newStart = this.projViewStart + delta;
        let newEnd = this.projViewEnd + delta;
        if (newStart < totalStart) {
            newStart = totalStart;
            newEnd = totalStart + span;
        }
        if (newEnd > totalEnd) {
            newEnd = totalEnd;
            newStart = totalEnd - span;
        }
        this.projViewStart = newStart;
        this.projViewEnd = newEnd;
        this.saveCurrentProjectsZoom();
    }
    projZoomIn() {
        const allPoints = this.trendByProject.flatMap((s) => s.points);
        if (allPoints.length === 0 || this.projViewStart == null || this.projViewEnd == null) return;
        const totalStart = moment(allPoints[0].day).startOf('day').valueOf();
        const totalEnd = moment(allPoints[allPoints.length - 1].day)
            .endOf('day')
            .valueOf();
        const span = this.projViewEnd - this.projViewStart;
        const minSpan = 2 * 24 * 60 * 60 * 1000;
        const newSpan = Math.max(minSpan, Math.round(span * 0.75));
        const center = Math.round((this.projViewStart + this.projViewEnd) / 2);
        let newStart = center - Math.round(newSpan / 2);
        let newEnd = center + Math.round(newSpan / 2);
        if (newStart < totalStart) {
            newStart = totalStart;
            newEnd = newStart + newSpan;
        }
        if (newEnd > totalEnd) {
            newEnd = totalEnd;
            newStart = newEnd - newSpan;
        }
        this.projViewStart = newStart;
        this.projViewEnd = newEnd;
        this.saveCurrentProjectsZoom();
    }
    projZoomOut() {
        const allPoints = this.trendByProject.flatMap((s) => s.points);
        if (allPoints.length === 0 || this.projViewStart == null || this.projViewEnd == null) return;
        const totalStart = moment(allPoints[0].day).startOf('day').valueOf();
        const totalEnd = moment(allPoints[allPoints.length - 1].day)
            .endOf('day')
            .valueOf();
        const span = this.projViewEnd - this.projViewStart;
        const newSpan = Math.min(totalEnd - totalStart, Math.round(span / 0.75));
        const center = Math.round((this.projViewStart + this.projViewEnd) / 2);
        let newStart = center - Math.round(newSpan / 2);
        let newEnd = center + Math.round(newSpan / 2);
        if (newStart < totalStart) {
            newStart = totalStart;
            newEnd = newStart + newSpan;
        }
        if (newEnd > totalEnd) {
            newEnd = totalEnd;
            newStart = newEnd - newSpan;
        }
        this.projViewStart = newStart;
        this.projViewEnd = newEnd;
        this.saveCurrentProjectsZoom();
    }
    shiftPercent(percent: number) {
        const data = this.aggregatedDataForScrapTrend;
        if (data.length === 0) return;

        let startIdx: number;
        let endIdx: number;

        // Když jsou indexy null, znamená to zobrazit všechna data
        // Nastavíme výchozí okno a pak posuneme
        if (this.categoryStartIndex == null || this.categoryEndIndex == null) {
            const windowSize = Math.min(10, data.length);

            if (data.length <= windowSize) {
                return;
            }

            if (percent > 0) {
                startIdx = 0;
                endIdx = windowSize - 1;
            } else {
                startIdx = data.length - windowSize;
                endIdx = data.length - 1;
            }
        } else {
            startIdx = this.categoryStartIndex;
            endIdx = this.categoryEndIndex;
        }

        const visibleCount = endIdx - startIdx + 1;
        const calculatedShift = visibleCount * percent;
        const shift = percent > 0 ? Math.max(1, Math.round(calculatedShift)) : Math.min(-1, Math.round(calculatedShift));

        let newStart = startIdx + shift;
        let newEnd = endIdx + shift;

        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + visibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = newEnd - visibleCount + 1;
            if (newStart < 0) {
                newStart = 0;
                newEnd = data.length - 1;
            }
        }

        if (newStart === startIdx && newEnd === endIdx) {
            return;
        }

        this.categoryStartIndex = newStart;
        this.categoryEndIndex = newEnd;
    }
    zoomIn() {
        const data = this.aggregatedDataForScrapTrend;
        if (data.length === 0) return;

        const startIdx = this.categoryStartIndex ?? 0;
        const endIdx = this.categoryEndIndex ?? data.length - 1;
        const visibleCount = endIdx - startIdx + 1;

        const minVisible = 2;
        const newVisibleCount = Math.max(minVisible, Math.round(visibleCount * 0.75));

        const center = Math.round((startIdx + endIdx) / 2);
        let newStart = center - Math.round(newVisibleCount / 2);
        let newEnd = newStart + newVisibleCount - 1;

        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + newVisibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = Math.max(0, newEnd - newVisibleCount + 1);
        }

        this.categoryStartIndex = newStart;
        this.categoryEndIndex = newEnd;
    }
    zoomOut() {
        const data = this.aggregatedDataForScrapTrend;
        if (data.length === 0) return;

        const startIdx = this.categoryStartIndex ?? 0;
        const endIdx = this.categoryEndIndex ?? data.length - 1;
        const visibleCount = endIdx - startIdx + 1;

        const maxVisible = data.length;
        const newVisibleCount = Math.min(maxVisible, Math.round(visibleCount / 0.75));

        if (newVisibleCount >= data.length) {
            this.categoryStartIndex = null;
            this.categoryEndIndex = null;
            return;
        }

        const center = Math.round((startIdx + endIdx) / 2);
        let newStart = center - Math.round(newVisibleCount / 2);
        let newEnd = newStart + newVisibleCount - 1;

        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + newVisibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = Math.max(0, newEnd - newVisibleCount + 1);
        }

        this.categoryStartIndex = newStart;
        this.categoryEndIndex = newEnd;
    }

    // Heatmap helpers
    scrapDayName(di: number): string {
        const names = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
        return names[di] || '';
    }
    scrapCellColor(v: number): string {
        const max = Math.max(1, ...this.scrapGrid.reduce((a, r) => a.concat(r), [] as number[]));
        const ratio = v / max;
        if (v === 0) return this.scrapPalette[0];
        if (ratio >= 0.8) return this.scrapPalette[4];
        if (ratio >= 0.55) return this.scrapPalette[3];
        if (ratio >= 0.3) return this.scrapPalette[2];
        return this.scrapPalette[1];
    }
    get scrapTopHoursText() {
        return this.scrapTopHours.map((h) => `${h}:00`).join(', ');
    }
    get scrapCriticalDaysText() {
        return this.scrapCriticalDays.map((i) => this.scrapDayName(i)).join(', ');
    }

    get topScrapHeaders() {
        return [
            { text: this.$t('tpmAnalytics.reason') as any, value: 'reasonName', align: 'left' },
            { text: this.$t('tpmAnalytics.count') as any, value: 'count', align: 'left' },
            { text: this.$t('tpmAnalytics.share') as any, value: 'sharePct', align: 'left' },
            { text: this.$t('tpmAnalytics.change') as any, value: 'shareDeltaPct', align: 'left' },
            { text: this.$t('tpmAnalytics.actions') as any, value: 'action', sortable: false, align: 'right' },
        ];
    }

    get aggregatedScrapHeaders() {
        const headers: any[] = [];

        // Definovat pořadí dimenzí (stejné jako v toggle)
        const dimensionOrder: ScrapAggregationDimension[] = [
            'location',
            'workplace',
            'station',
            'project',
            'shiftTeam',
            'shiftType',
            'scrapReason',
        ];

        // Přidat sloupce podle pořadí dimenzí, ale pouze pro vybrané
        for (const dim of dimensionOrder) {
            if (!this.selectedScrapDimensions.includes(dim)) continue;

            switch (dim) {
                case 'location':
                    headers.push({ text: this.$t('tpmAnalytics.dimensionLocation'), value: 'locationName', align: 'left' });
                    break;
                case 'workplace':
                    headers.push({ text: this.$t('tpmAnalytics.dimensionWorkplace'), value: 'workplaceName', align: 'left' });
                    break;
                case 'station':
                    headers.push({ text: this.$t('tpmAnalytics.dimensionStation'), value: 'stationName', align: 'left' });
                    break;
                case 'project':
                    headers.push({ text: this.$t('tpmAnalytics.scrapDimensionProject'), value: 'projectName', align: 'left' });
                    break;
                case 'shiftTeam':
                    headers.push({
                        text: this.$t('tpmAnalytics.scrapDimensionShiftTeam'),
                        value: 'shiftTeamTypeName',
                        align: 'left',
                    });
                    break;
                case 'shiftType':
                    headers.push({
                        text: this.$t('tpmAnalytics.scrapDimensionShiftType'),
                        value: 'shiftTypeName',
                        align: 'left',
                    });
                    break;
                case 'scrapReason':
                    headers.push({
                        text: this.$t('tpmAnalytics.scrapDimensionScrapReason'),
                        value: 'scrapReasonName',
                        align: 'left',
                    });
                    break;
            }
        }

        // Přidat standardní sloupce
        headers.push({ text: this.$t('tpmAnalytics.totalScraps'), value: 'totalScraps', align: 'left' });
        headers.push({ text: this.$t('tpmAnalytics.eventCount'), value: 'eventCount', align: 'left' });
        headers.push({ text: this.$t('tpmAnalytics.percentage'), value: 'percentage', align: 'left' });
        headers.push({ text: this.$t('tpmAnalytics.cumulativePercentage'), value: 'cumulativePercentage', align: 'left' });
        headers.push({ text: this.$t('tpmAnalytics.change'), value: 'changePct', align: 'left' });

        return headers;
    }

    get aggregatedScrapDataSorted() {
        const items = [...this.aggregatedScrapData];

        // Seřadit podle vybraného kritéria sestupně
        const sorted =
            this.scrapSortBy === 'eventCount'
                ? items.sort((a, b) => (b.eventCount ?? 0) - (a.eventCount ?? 0))
                : items.sort((a, b) => (b.totalScraps ?? 0) - (a.totalScraps ?? 0));

        // Omezit na TOP N záznamů
        const limited = sorted.slice(0, this.aggregatedScrapTop);

        // Vypočítat celkové hodnoty POUZE z omezených (TOP X) záznamů
        const totalScraps = limited.reduce((sum, item) => sum + (item.totalScraps ?? 0), 0);
        const totalEvents = limited.reduce((sum, item) => sum + (item.eventCount ?? 0), 0);

        // Vypočítat kumulativní podíl a přiřadit ABC kategorii
        let cumulativeValue = 0;
        const itemsWithAnalysis = limited.map((item, index) => {
            const value = this.scrapSortBy === 'eventCount' ? item.eventCount ?? 0 : item.totalScraps ?? 0;
            const total = this.scrapSortBy === 'eventCount' ? totalEvents : totalScraps;
            const percentage = total > 0 ? (value / total) * 100 : 0;
            cumulativeValue += value;
            const cumulativePercentage = total > 0 ? (cumulativeValue / total) * 100 : 0;

            // ABC kategorizace podle kumulativního podílu
            let abcCategory = 'C';
            let abcColor = '#4caf50'; // zelená pro C
            if (cumulativePercentage <= 80) {
                abcCategory = 'A';
                abcColor = '#f44336'; // červená pro A
            } else if (cumulativePercentage <= 95) {
                abcCategory = 'B';
                abcColor = '#ff9800'; // oranžová pro B
            }

            return {
                ...item,
                percentage,
                cumulativePercentage,
                abcCategory,
                abcColor,
            };
        });

        return itemsWithAnalysis;
    }

    // Vytvořit label pro TreeMap buňku (přeskočit hodnoty "--")
    createScrapTreemapLabel(item: any): string {
        const parts: string[] = [];
        for (const dim of this.selectedScrapDimensions) {
            let value = '';
            switch (dim) {
                case 'location':
                    value = item.locationName || '';
                    break;
                case 'workplace':
                    value = item.workplaceName || '';
                    break;
                case 'station':
                    value = item.stationName || '';
                    break;
                case 'project':
                    value = item.projectName || '';
                    break;
                case 'shiftTeam':
                    value = item.shiftTeamTypeName || '';
                    break;
                case 'shiftType':
                    value = item.shiftTypeName || '';
                    break;
                case 'scrapReason':
                    value = item.scrapReasonName || '';
                    break;
            }
            // Přidat pouze neprázdné hodnoty a ne "--"
            if (value && value !== '--') {
                parts.push(value);
            }
        }
        return parts.join(' / ');
    }

    get scrapTreemapSeries() {
        const data = this.aggregatedScrapDataSorted.map((item) => ({
            x: this.createScrapTreemapLabel(item),
            y: this.scrapSortBy === 'eventCount' ? item.eventCount : item.totalScraps,
            fillColor: item.abcColor,
            meta: item, // Pro tooltip
        }));

        return [{ data }];
    }

    get scrapTreemapOptions() {
        return {
            chart: {
                type: 'treemap',
                toolbar: { show: false },
            },
            legend: {
                show: false,
            },
            dataLabels: {
                enabled: true,
                style: {
                    fontSize: '12px',
                    fontWeight: 'bold',
                    colors: ['#fff'],
                },
                formatter: (text: string, opt: any) => {
                    return text;
                },
            },
            plotOptions: {
                treemap: {
                    enableShades: false,
                    distributed: true,
                    colorScale: {
                        ranges: [],
                    },
                },
            },
            tooltip: {
                custom: ({ seriesIndex, dataPointIndex, w }: any) => {
                    const item = w.config.series[seriesIndex].data[dataPointIndex].meta;
                    const parts: string[] = [];

                    // Sestavit tooltip s kompletními informacemi (včetně "--")
                    for (const dim of this.selectedScrapDimensions) {
                        switch (dim) {
                            case 'location':
                                parts.push(`<b>${this.$t('tpmAnalytics.dimensionLocation')}:</b> ${item.locationName || '--'}`);
                                break;
                            case 'workplace':
                                parts.push(
                                    `<b>${this.$t('tpmAnalytics.dimensionWorkplace')}:</b> ${item.workplaceName || '--'}`
                                );
                                break;
                            case 'station':
                                parts.push(`<b>${this.$t('tpmAnalytics.dimensionStation')}:</b> ${item.stationName || '--'}`);
                                break;
                            case 'project':
                                parts.push(
                                    `<b>${this.$t('tpmAnalytics.scrapDimensionProject')}:</b> ${item.projectName || '--'}`
                                );
                                break;
                            case 'shiftTeam':
                                parts.push(
                                    `<b>${this.$t('tpmAnalytics.scrapDimensionShiftTeam')}:</b> ${
                                        item.shiftTeamTypeName || '--'
                                    }`
                                );
                                break;
                            case 'shiftType':
                                parts.push(
                                    `<b>${this.$t('tpmAnalytics.scrapDimensionShiftType')}:</b> ${item.shiftTypeName || '--'}`
                                );
                                break;
                            case 'scrapReason':
                                parts.push(
                                    `<b>${this.$t('tpmAnalytics.scrapDimensionScrapReason')}:</b> ${
                                        item.scrapReasonName || '--'
                                    }`
                                );
                                break;
                        }
                    }

                    parts.push(`<b>${this.$t('tpmAnalytics.totalScraps')}:</b> ${item.totalScraps}`);
                    parts.push(`<b>${this.$t('tpmAnalytics.eventCount')}:</b> ${item.eventCount}`);
                    parts.push(`<b>${this.$t('tpmAnalytics.percentage')}:</b> ${item.percentage.toFixed(1)}%`);

                    return `<div style="padding: 8px; background: white; border: 1px solid #e0e0e0; border-radius: 4px;">
                        ${parts.join('<br/>')}
                    </div>`;
                },
            },
        } as any;
    }

    formatAxisLabel(ts: number, gran: 'day' | 'week' | 'month'): string {
        const m = moment.tz(ts, DISPLAY_TIMEZONE);
        if (!m.isValid()) return '';
        if (gran === 'month') return m.format('MM/YYYY');
        if (gran === 'week') {
            const start = m.clone().startOf('isoWeek');
            const end = m.clone().endOf('isoWeek');
            const week = m.isoWeek();
            const weekLabel = `${week}. týden`;
            const dateRange = `(${start.format('DD.MM.')} - ${end.format('DD.MM.')})`;
            return weekLabel + '|' + dateRange;
        }
        return m.format('D.M.');
    }
    get granularityItems() {
        return [
            { text: this.$t('tpmAnalytics.granularityDay') as any, value: 'day' },
            { text: this.$t('tpmAnalytics.granularityWeek') as any, value: 'week' },
            { text: this.$t('tpmAnalytics.granularityMonth') as any, value: 'month' },
        ] as any;
    }

    saveCurrentZoom() {
        if (this.viewStart != null && this.viewEnd != null && this.trend.length > 0) {
            const unit = this.getTimeUnit(this.scrapGranularity);
            const dataStart = moment(this.trend[0].day).startOf(unit).valueOf();
            const dataEnd = moment(this.trend[this.trend.length - 1].day)
                .endOf(unit)
                .valueOf();
            saveChartZoom('scrap-trend', this.scrapGranularity, this.viewStart, this.viewEnd, dataStart, dataEnd, unit);
        }
    }

    saveCurrentProjectsZoom() {
        const allPoints = this.trendByProject.flatMap((s) => s.points);
        if (this.projViewStart != null && this.projViewEnd != null && allPoints.length > 0) {
            const unit = this.getTimeUnit(this.scrapGranularity);
            const dataStart = moment(allPoints[0].day).startOf(unit).valueOf();
            const dataEnd = moment(allPoints[allPoints.length - 1].day)
                .endOf(unit)
                .valueOf();
            saveChartZoom(
                'scrap-projects',
                this.scrapGranularity,
                this.projViewStart,
                this.projViewEnd,
                dataStart,
                dataEnd,
                unit
            );
        }
    }

    saveCurrentWorkplacesZoom() {
        const allPoints = this.trendByWorkplace.flatMap((s) => s.points);
        if (this.wpViewStart != null && this.wpViewEnd != null && allPoints.length > 0) {
            const unit = this.getTimeUnit(this.scrapGranularity);
            const dataStart = moment(allPoints[0].day).startOf(unit).valueOf();
            const dataEnd = moment(allPoints[allPoints.length - 1].day)
                .endOf(unit)
                .valueOf();
            saveChartZoom(
                'scrap-workplaces',
                this.scrapGranularity,
                this.wpViewStart,
                this.wpViewEnd,
                dataStart,
                dataEnd,
                unit
            );
        }
    }

    saveCurrentTeamsZoom() {
        const allPoints = this.trendByShiftTeam.flatMap((s: any) => s.points);
        if (this.teamViewStart != null && this.teamViewEnd != null && allPoints.length > 0) {
            const unit = this.getTimeUnit(this.scrapGranularity);
            const dataStart = moment(allPoints[0].day).startOf(unit).valueOf();
            const dataEnd = moment(allPoints[allPoints.length - 1].day)
                .endOf(unit)
                .valueOf();
            saveChartZoom('scrap-teams', this.scrapGranularity, this.teamViewStart, this.teamViewEnd, dataStart, dataEnd, unit);
        }
    }

    saveCurrentShiftTypesZoom() {
        const allPoints = this.trendByShiftType.flatMap((s) => s.points);
        if (this.stViewStart != null && this.stViewEnd != null && allPoints.length > 0) {
            const unit = this.getTimeUnit(this.scrapGranularity);
            const dataStart = moment(allPoints[0].day).startOf(unit).valueOf();
            const dataEnd = moment(allPoints[allPoints.length - 1].day)
                .endOf(unit)
                .valueOf();
            saveChartZoom(
                'scrap-shifttypes',
                this.scrapGranularity,
                this.stViewStart,
                this.stViewEnd,
                dataStart,
                dataEnd,
                unit
            );
        }
    }
}
